import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { toast } from 'sonner';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ImageCropUploader, CroppedImage } from '../components/common/ImageCropUploader';
import { CATEGORY_ICONS, CATEGORY_COLORS, CategoryIcon, DEFAULT_CATEGORY_ICON, DEFAULT_CATEGORY_COLOR } from '../lib/categoryIcons';
import { ALLERGENS } from '../lib/allergens';
import { DIET_TAGS, DIET_TAG_META, dietIconSvg } from '../lib/dietTags';
import { LANGUAGES } from '../i18n/languages';
import { fmtEur } from '../lib/constants';
import { useConfirm } from '../context/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import { AiMenuImportDialog } from '../components/menu/AiMenuImportDialog';
import { SubcategoryManagerDialog } from '../components/menu/SubcategoryManagerDialog';
import { cn } from '../lib/utils';
import { clearMenuCache } from '../lib/menuCache';
import { Plus, Pencil, Trash2, X, ImageOff, Check, ChefHat, Wine, Eye, EyeOff, Search, Sparkles, Layers, GripVertical, Move, Map, Store, Barcode } from 'lucide-react';
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Fixed proportions shared by every product image.
const PRODUCT_ASPECT = '4 / 3';
const STATIONS = [
  { value: 'cucina', labelKey: 'menu.kitchen', Icon: ChefHat },
  { value: 'bar', labelKey: 'menu.bar', Icon: Wine },
];
const blankCat = { name: '', icon: DEFAULT_CATEGORY_ICON, color: DEFAULT_CATEGORY_COLOR, image: null, image_x: 50, image_y: 50, image_zoom: 1, published: true };

// Riquadro categoria (presentazionale). Se `dragProps` è passato, l'intero
// riquadro diventa trascinabile per il riordino (drag-and-drop stile home screen).
const CategoryCardInner = React.forwardRef(function CategoryCardInner(
  { c, t, subCount, onSubcats, onTogglePublish, onEdit, onDelete, dragProps, style, dragging, draggable },
  ref,
) {
  return (
    <div
      ref={ref}
      style={style}
      className={cn(
        'group relative h-32 overflow-hidden rounded-xl border border-border shadow-[var(--shadow-sm)]',
        draggable && 'menu-jiggle cursor-grab active:cursor-grabbing touch-manipulation select-none',
        dragging && 'ring-2 ring-primary shadow-[var(--shadow-lg)]',
      )}
      data-testid={`category-box-${c.id}`}
      {...(dragProps || {})}
    >
      {c.image ? (
        <img
          src={c.image}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: `${c.image_x ?? 50}% ${c.image_y ?? 50}%`, transform: `scale(${c.image_zoom ?? 1})` }}
        />
      ) : (
        <div className="absolute inset-0" style={{ background: c.color || 'hsl(var(--muted))' }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/5" />
      <div className="absolute left-2 top-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/90 shadow-sm backdrop-blur-sm">
          <CategoryIcon iconKey={c.icon} color={c.color} className="h-5 w-5" />
        </span>
      </div>
      {draggable && (
        <div className="absolute left-1/2 top-2 -translate-x-1/2 rounded-md bg-black/45 px-1.5 py-0.5 text-white/90 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden>
          <GripVertical className="h-4 w-4" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-3">
        <span className="block font-display text-lg font-extrabold uppercase tracking-wide text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.85)]">
          {c.name}
        </span>
        <div className="mt-1 flex items-center gap-1.5">
          {c.published === false && <span className="inline-flex items-center gap-1 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white"><EyeOff className="h-3 w-3" /> {t('menu.outOfMenu')}</span>}
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => onSubcats(c)}
            data-testid={`category-subcats-${c.id}`}
            className="inline-flex items-center gap-1 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold text-foreground hover:bg-white transition-colors"
          >
            <Layers className="h-3 w-3" /> {t('subcat.manage')}
            {subCount > 0 && (
              <span className="ml-0.5 rounded-full bg-primary/15 text-primary px-1">{subCount}</span>
            )}
          </button>
        </div>
      </div>
      <div className="absolute right-2 top-2 flex gap-1" onPointerDown={(e) => e.stopPropagation()}>
        <Button variant="secondary" size="icon" className="h-8 w-8 rounded-lg bg-white/85 text-foreground hover:bg-white shadow-sm" onClick={() => onTogglePublish(c)} aria-label={t('menu.showHideDigital')} title={c.published !== false ? t('menu.visibleInDigitalCat') : t('menu.hiddenFromDigitalCat')}>
          {c.published !== false ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4" />}
        </Button>
        <Button variant="secondary" size="icon" className="h-8 w-8 rounded-lg bg-white/85 text-foreground hover:bg-white shadow-sm" onClick={() => onEdit(c)} data-testid={`category-edit-${c.id}`} aria-label={`${t('menu.editCategory')} ${c.name}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="secondary" size="icon" className="h-8 w-8 rounded-lg bg-white/85 text-destructive hover:bg-white shadow-sm" onClick={() => onDelete(c)} aria-label={`${t('menu.delete')} ${c.name}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});

// Wrapper trascinabile: collega il riquadro a dnd-kit (useSortable).
const SortableCategoryCard = ({ c, ...rest }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.9 : 1,
  };
  return (
    <CategoryCardInner
      ref={setNodeRef}
      c={c}
      style={style}
      dragging={isDragging}
      draggable
      dragProps={{ ...attributes, ...listeners }}
      {...rest}
    />
  );
};

// Tile prodotto trascinabile (modalità riordino, stile schermata home).
// Mostra solo immagine + nome + prezzo; l'intero riquadro è la maniglia di trascinamento.
const SortableProductTile = ({ p }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: p.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'menu-jiggle relative flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left shadow-[var(--shadow-sm)] cursor-grab active:cursor-grabbing touch-manipulation select-none',
        isDragging && 'z-10 ring-2 ring-primary shadow-[var(--shadow-lg)]',
      )}
      data-testid={`sortable-product-${p.id}`}
      {...attributes}
      {...listeners}
    >
      <div className="relative">
        {p.image ? (
          <CroppedImage data={p} aspect={PRODUCT_ASPECT} draggable={false} />
        ) : (
          <div className="flex items-center justify-center bg-muted/50 text-muted-foreground" style={{ aspectRatio: PRODUCT_ASPECT }}>
            <ImageOff className="h-7 w-7 opacity-50" />
          </div>
        )}
        <span className="absolute right-2 top-2 rounded-lg bg-foreground/70 p-1 text-background backdrop-blur-sm">
          <GripVertical className="h-4 w-4" />
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 p-3">
        <span className="min-w-0 truncate font-medium text-sm">{p.name}</span>
        <span className="font-display font-semibold text-primary tabular shrink-0">{fmtEur(p.price)}</span>
      </div>
    </div>
  );
};

export default function MenuPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [importOpen, setImportOpen] = useState(false);
  const [menuTab, setMenuTab] = useState('products');
  const [subMgrCat, setSubMgrCat] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [catFilter, setCatFilter] = useState('all');
  const [prodSearch, setProdSearch] = useState('');
  const [catSearch, setCatSearch] = useState('');
  const [prodDialog, setProdDialog] = useState(false);
  const [editingProd, setEditingProd] = useState(null);
  const [catForm, setCatForm] = useState(blankCat);
  const [editingCat, setEditingCat] = useState(null);
  const [catLang, setCatLang] = useState('it');
  const catFormRef = useRef(null);
  const confirm = useConfirm();
  // Modalità riordino (stile schermata home): attivata da un pulsante dedicato.
  // Quando è attiva, categorie e prodotti diventano trascinabili (e "tremolano");
  // fuori da questa modalità si clicca normalmente per aprire/modificare.
  const [reorderMode, setReorderMode] = useState(false);

  const load = async () => {
    // Le modifiche al menu invalidano la cache POS (drawer tavoli/negozio),
    // così la prossima apertura ricarica dati aggiornati.
    clearMenuCache();
    const [c, p, i, cl, s] = await Promise.all([
      api.get('/categories'), api.get('/products'), api.get('/ingredients'), api.get('/category-language'), api.get('/subcategories'),
    ]);
    setCategories(c.data); setProducts(p.data); setIngredients(i.data); setSubcategories(s.data);
    setCatLang(cl.data?.language || 'it');
  };
  useEffect(() => { load(); }, []);

  const changeCatLang = async (code) => {
    setCatLang(code);
    try { await api.put('/category-language', { language: code }); toast.success(t('menu.categoryLanguageSaved')); }
    catch (e) { toast.error(t('menu.catSaveError')); }
  };

  const filtered = useMemo(() => {
    const q = prodSearch.trim().toLowerCase();
    return products.filter((p) =>
      (catFilter === 'all' || p.category_id === catFilter) &&
      (!q || (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q))
    );
  }, [products, catFilter, prodSearch]);
  const filteredCats = useMemo(() => {
    const q = catSearch.trim().toLowerCase();
    return q ? categories.filter((c) => (c.name || '').toLowerCase().includes(q)) : categories;
  }, [categories, catSearch]);
  const catName_ = (id) => categories.find((c) => c.id === id)?.name || '\u2014';

  const toggleAvail = async (p) => {
    await api.patch(`/products/${p.id}/availability`, null, { params: { available: !p.available } });
    load();
  };
  const toggleProductPublish = async (p) => {
    await api.patch(`/products/${p.id}/publish`, null, { params: { published: !(p.published !== false) } });
    load();
    toast.success(p.published !== false ? t('menu.removedFromDigital') : t('menu.addedToDigital'));
  };
  const toggleCatPublish = async (c) => {
    await api.patch(`/categories/${c.id}/publish`, null, { params: { published: !(c.published !== false) } });
    load();
  };
  const delProduct = async (p) => { if (await confirm({ title: t('menu.deleteProductConfirm', { name: p.name }), destructive: true, confirmText: t('menu.delete') })) { await api.delete(`/products/${p.id}`); load(); toast.success(t('menu.productDeleted')); } };

  const resetCatForm = () => { setCatForm(blankCat); setEditingCat(null); };
  const editCat = (c) => {
    setEditingCat(c);
    setCatForm({ name: c.name, icon: c.icon || DEFAULT_CATEGORY_ICON, color: c.color || DEFAULT_CATEGORY_COLOR, image: c.image || null, image_x: c.image_x ?? 50, image_y: c.image_y ?? 50, image_zoom: c.image_zoom ?? 1, published: c.published !== false });
    setTimeout(() => catFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const saveCat = async () => {
    if (!catForm.name.trim()) { toast.error(t('menu.catNameRequired')); return; }
    const payload = {
      name: catForm.name,
      color: catForm.color || DEFAULT_CATEGORY_COLOR,
      icon: catForm.icon || DEFAULT_CATEGORY_ICON,
      order: editingCat?.order ?? 0,
      image: catForm.image || null,
      image_x: catForm.image_x ?? 50,
      image_y: catForm.image_y ?? 50,
      image_zoom: catForm.image_zoom ?? 1,
      published: catForm.published !== false,
    };
    try {
      if (editingCat) await api.put(`/categories/${editingCat.id}`, payload);
      else await api.post('/categories', payload);
      resetCatForm(); load(); toast.success(t('menu.categorySaved'));
    } catch (e) { toast.error(t('menu.catSaveError')); }
  };
  const delCat = async (c) => { if (await confirm({ title: t('menu.deleteCategoryConfirm', { name: c.name }), destructive: true, confirmText: t('menu.delete') })) { await api.delete(`/categories/${c.id}`); load(); } };

  // --- Riordino categorie via drag-and-drop (dnd-kit) ---
  // Attivo solo in "modalità riordino" e senza ricerca (riordinare un
  // sottoinsieme filtrato sarebbe ambiguo).
  const catsSortable = reorderMode && catSearch.trim() === '';
  const dndSensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
  );
  const handleCatDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(categories, oldIndex, newIndex);
    setCategories(next); // aggiornamento ottimistico immediato
    try {
      const { data } = await api.put('/categories/reorder', { ids: next.map((c) => c.id) });
      if (Array.isArray(data)) setCategories(data);
      toast.success(t('menu.orderSaved', { defaultValue: 'Ordine aggiornato' }));
    } catch (e) {
      toast.error(t('menu.orderError', { defaultValue: 'Errore nel salvataggio dell\u2019ordine' }));
      load();
    }
  };

  // --- Riordino PRODOTTI via drag-and-drop (per singola categoria) ---
  // I prodotti sono raggruppati per categoria in POS/Carta/PDF, quindi il
  // riordino ha senso solo dentro UNA categoria: richiede una categoria
  // selezionata nel filtro e nessuna ricerca attiva.
  const prodsSortable = reorderMode && catFilter !== 'all' && prodSearch.trim() === '';
  const handleProdDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = filtered.findIndex((p) => p.id === active.id);
    const newIndex = filtered.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const nextList = arrayMove(filtered, oldIndex, newIndex);
    // Aggiornamento ottimistico: riscrive l'ordine locale dei prodotti della categoria.
    setProducts((prev) => {
      const orderMap = new Map(nextList.map((p, i) => [p.id, i]));
      return prev.map((p) => (orderMap.has(p.id) ? { ...p, order: orderMap.get(p.id) } : p))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.name || '').localeCompare(b.name || ''));
    });
    try {
      await api.put('/products/reorder', { ids: nextList.map((p) => p.id) });
      toast.success(t('menu.orderSaved', { defaultValue: 'Ordine aggiornato' }));
    } catch (e) {
      toast.error(t('menu.orderError', { defaultValue: 'Errore nel salvataggio dell\u2019ordine' }));
      load();
    }
  };

  return (
    <div className="p-4 md:p-6">
      <PageHeader title={t('menu.title')} subtitle={t('menu.subtitle')} />
      <AiMenuImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
      <Tabs value={menuTab} onValueChange={setMenuTab}>
        {/* Barra strumenti: nella scheda Prodotti i pulsanti sono distribuiti equamente
            (come nella pagina Tavoli), con "Cerca prodotto" tutto a sinistra. */}
        <div className={cn('flex flex-wrap items-center gap-2 mb-4', menuTab === 'products' && 'justify-between')}>
          {menuTab === 'products' ? (
            <>
              {/* Cerca prodotto: tutto a sinistra */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder={t('menu.searchProduct')} className="pl-9" data-testid="product-search-input" />
              </div>
              <TabsList>
                <TabsTrigger value="products">{t('menu.products')}</TabsTrigger>
                <TabsTrigger value="categories">{t('menu.categories')}</TabsTrigger>
              </TabsList>
              <Select value={catFilter} onValueChange={setCatFilter}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('menu.allCategories')}</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {isAdmin && (
                <Button variant="outline" className="rounded-xl" onClick={() => setImportOpen(true)} data-testid="open-ai-import">
                  <Sparkles className="h-4 w-4 mr-2" /> {t('cartaDigitale.importAi')}
                </Button>
              )}
              <Button className="rounded-xl" data-testid="add-product-button" onClick={() => { setEditingProd(null); setProdDialog(true); }}><Plus className="h-4 w-4 mr-2" /> {t('menu.product')}</Button>
              <Button variant={reorderMode ? 'default' : 'outline'} className="rounded-xl" onClick={() => setReorderMode((v) => !v)} data-testid="reorder-toggle-button">
                {reorderMode ? <><Check className="h-4 w-4 mr-2" /> {t('menu.reorderDone', { defaultValue: 'Fine' })}</> : <><Move className="h-4 w-4 mr-2" /> {t('menu.reorder', { defaultValue: 'Riordina' })}</>}
              </Button>
            </>
          ) : (
            <>
              <TabsList>
                <TabsTrigger value="products">{t('menu.products')}</TabsTrigger>
                <TabsTrigger value="categories">{t('menu.categories')}</TabsTrigger>
              </TabsList>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input value={catSearch} onChange={(e) => setCatSearch(e.target.value)} placeholder={t('menu.searchCategory')} className="pl-9" data-testid="category-search-input" />
              </div>
              <Button variant={reorderMode ? 'default' : 'outline'} className="rounded-xl" onClick={() => setReorderMode((v) => !v)} data-testid="reorder-toggle-button-cats">
                {reorderMode ? <><Check className="h-4 w-4 mr-2" /> {t('menu.reorderDone', { defaultValue: 'Fine' })}</> : <><Move className="h-4 w-4 mr-2" /> {t('menu.reorder', { defaultValue: 'Riordina' })}</>}
              </Button>
            </>
          )}
        </div>

        {/* ---------------- PRODUCTS ---------------- */}
        <TabsContent value="products" className="mt-4">
          {reorderMode && (
            <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="product-reorder-hint">
              <GripVertical className="h-3.5 w-3.5" />
              {prodsSortable
                ? t('menu.reorderProductsHint', { defaultValue: 'Trascina i prodotti per cambiarne l\u2019ordine: sarà applicato a TPV, Carta Digitale e menu cartaceo.' })
                : t('menu.reorderProductsPickCat', { defaultValue: 'Seleziona una categoria (nel filtro in alto) per riordinare i suoi prodotti.' })}
            </p>
          )}
          {prodsSortable ? (
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleProdDragEnd}>
              <SortableContext items={filtered.map((p) => p.id)} strategy={rectSortingStrategy}>
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }} data-testid="product-grid-sortable">
                  {filtered.map((p) => <SortableProductTile key={p.id} p={p} />)}
                  {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10">{t('menu.noProducts')}</div>}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
            {filtered.map((p) => (
              <Card key={p.id} className="overflow-hidden p-0" data-testid={`product-card-${p.id}`}>
                <div className="relative">
                  {p.image ? (
                    <CroppedImage data={p} aspect={PRODUCT_ASPECT} />
                  ) : (
                    <div className="flex items-center justify-center bg-muted/50 text-muted-foreground" style={{ aspectRatio: PRODUCT_ASPECT }}>
                      <ImageOff className="h-7 w-7 opacity-50" />
                    </div>
                  )}
                  {p.published === false && (
                    <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-lg bg-foreground/80 px-2 py-1 text-[11px] font-semibold text-background backdrop-blur-sm">
                      <EyeOff className="h-3 w-3" /> {t('menu.outOfMenu')}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{catName_(p.category_id)}</div>
                    </div>
                    <div className="font-display font-semibold text-primary tabular">{fmtEur(p.price)}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.sales_channel === 'negozio' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-medium bg-accent/10 text-accent">
                        <Store className="h-3 w-3" /> {t('menu.channelStore', { defaultValue: 'Vendita negozio' })}
                      </span>
                    ) : (
                    <span className={cn('inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded font-medium', (p.station || 'cucina') === 'bar' ? 'bg-accent/10 text-accent' : 'bg-primary/10 text-primary')}>
                      {(p.station || 'cucina') === 'bar' ? <Wine className="h-3 w-3" /> : <ChefHat className="h-3 w-3" />}
                      {(p.station || 'cucina') === 'bar' ? t('menu.bar') : t('menu.kitchen')}
                    </span>
                    )}
                    {p.allergens?.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded bg-muted">{t('menu.allergensCount', { count: p.allergens.length })}</span>}
                    {p.variants?.map((v) => <span key={v.name} className="text-[11px] px-2 py-0.5 rounded bg-muted">{v.name}</span>)}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <Switch checked={p.available} onCheckedChange={() => toggleAvail(p)} aria-label={p.available ? t('menu.available') : t('menu.soldOut')} title={p.available ? t('menu.available') : t('menu.soldOut')} />
                    <Button variant="ghost" size="icon" title={p.published !== false ? t('menu.visibleInDigital') : t('menu.hiddenFromDigital')} className={p.published !== false ? 'text-primary' : 'text-muted-foreground'} onClick={() => toggleProductPublish(p)} data-testid="product-publish-toggle">
                      {p.published !== false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => { setEditingProd(p); setProdDialog(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => delProduct(p)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
            {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10">{t('menu.noProducts')}</div>}
          </div>
          )}
        </TabsContent>

        {/* ---------------- CATEGORIES ---------------- */}
        <TabsContent value="categories" className="mt-4">
          <Card className="p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="category-language-card">
            <div className="flex-1 min-w-[220px]">
              <div className="font-semibold text-sm">{t('menu.categoryLanguage')}</div>
              <div className="text-xs text-muted-foreground">{t('menu.categoryLanguageHint')}</div>
            </div>
            <Select value={catLang} onValueChange={changeCatLang}>
              <SelectTrigger className="w-52" data-testid="category-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    <span className="mr-2" aria-hidden>{l.flag}</span>{l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>
          <div className="grid gap-4 lg:grid-cols-[360px_1fr]">
            <Card ref={catFormRef} className="p-5 h-fit scroll-mt-4">
              <div className="font-semibold mb-3">{editingCat ? t('menu.editCategory') : t('menu.newCategory')}</div>
              <div className="space-y-3">
                <div>
                  <Label className="mb-1.5 block">{t('menu.name')}</Label>
                  <Input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder={t('menu.catNamePlaceholder')} data-testid="category-name-input" />
                </div>

                {/* Icon (logo) picker */}
                <div>
                  <Label className="mb-1.5 block">{t('menu.categoryLogo')}</Label>
                  <div className="grid grid-cols-8 gap-1.5 rounded-xl border border-border p-2 max-h-44 overflow-y-auto" data-testid="category-icon-picker">
                    {CATEGORY_ICONS.map(({ key, label, Icon }) => {
                      const active = catForm.icon === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          title={label}
                          aria-label={label}
                          aria-pressed={active}
                          onClick={() => setCatForm({ ...catForm, icon: key })}
                          data-testid={`category-icon-${key}`}
                          className={`flex h-9 w-full items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? 'border-transparent ring-2 ring-offset-1 ring-offset-background' : 'border-border hover:bg-muted'}`}
                          style={active ? { backgroundColor: `${catForm.color}1a`, '--tw-ring-color': catForm.color } : undefined}
                        >
                          <Icon className="h-4 w-4" style={{ color: catForm.color }} />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Color picker */}
                <div>
                  <Label className="mb-1.5 block">{t('menu.logoColor')}</Label>
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="category-color-picker">
                    {CATEGORY_COLORS.map((col) => (
                      <button
                        key={col}
                        type="button"
                        aria-label={`Colore ${col}`}
                        onClick={() => setCatForm({ ...catForm, color: col })}
                        data-testid={`category-color-${col}`}
                        className="flex h-7 w-7 items-center justify-center rounded-full ring-offset-2 ring-offset-background transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        style={{ backgroundColor: col, boxShadow: catForm.color === col ? `0 0 0 2px var(--background), 0 0 0 4px ${col}` : 'none' }}
                      >
                        {catForm.color === col && <Check className="h-3.5 w-3.5 text-white" />}
                      </button>
                    ))}
                    <label className="relative ml-1 flex h-7 cursor-pointer items-center gap-1.5 rounded-full border border-border px-2 text-xs text-muted-foreground hover:bg-muted" title={t('menu.customColor')}>
                      <span className="h-4 w-4 rounded-full border border-border" style={{ backgroundColor: catForm.color }} />
                      {t('menu.other')}
                      <input type="color" value={catForm.color} onChange={(e) => setCatForm({ ...catForm, color: e.target.value })} className="absolute inset-0 cursor-pointer opacity-0" data-testid="category-color-custom" />
                    </label>
                  </div>
                </div>

                <ImageCropUploader
                  value={catForm}
                  onChange={setCatForm}
                  aspect="16 / 9"
                  label={t('menu.backgroundImage')}
                  hint={t('menu.backgroundHint')}
                  testId="category-image-uploader"
                />
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div>
                    <div className="text-sm font-medium">{t('menu.showInDigital')}</div>
                    <div className="text-xs text-muted-foreground">{t('menu.showCategoryDesc')}</div>
                  </div>
                  <Switch checked={catForm.published !== false} onCheckedChange={(v) => setCatForm({ ...catForm, published: v })} data-testid="category-publish-switch" />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button onClick={saveCat} className="rounded-xl" data-testid="category-save-button">{editingCat ? t('menu.update') : t('menu.add')}</Button>
                  {editingCat && <Button variant="ghost" className="rounded-xl" onClick={resetCatForm}><X className="h-4 w-4 mr-1" /> {t('menu.cancel')}</Button>}
                </div>
              </div>
            </Card>

            <div>
              {catsSortable && categories.length > 1 && (
                <p className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="category-reorder-hint">
                  <GripVertical className="h-3.5 w-3.5" />
                  {t('menu.reorderHint', { defaultValue: 'Trascina le categorie per cambiarne l\u2019ordine: sarà applicato anche a TPV, Carta Digitale e menu cartaceo.' })}
                </p>
              )}
              {catsSortable ? (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleCatDragEnd}>
                  <SortableContext items={categories.map((c) => c.id)} strategy={rectSortingStrategy}>
                    <div className="grid gap-3 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }} data-testid="category-grid">
                      {categories.map((c) => (
                        <SortableCategoryCard
                          key={c.id}
                          c={c}
                          t={t}
                          subCount={subcategories.filter((s) => s.category_id === c.id).length}
                          onSubcats={setSubMgrCat}
                          onTogglePublish={toggleCatPublish}
                          onEdit={editCat}
                          onDelete={delCat}
                        />
                      ))}
                      {categories.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10">{t('menu.noCategories')}</div>}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="grid gap-3 content-start" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                  {filteredCats.map((c) => (
                    <CategoryCardInner
                      key={c.id}
                      c={c}
                      t={t}
                      subCount={subcategories.filter((s) => s.category_id === c.id).length}
                      onSubcats={setSubMgrCat}
                      onTogglePublish={toggleCatPublish}
                      onEdit={editCat}
                      onDelete={delCat}
                    />
                  ))}
                  {filteredCats.length === 0 && <div className="col-span-full text-center text-muted-foreground py-10">{t('menu.noCategories')}</div>}
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ProductDialog open={prodDialog} onOpenChange={setProdDialog} product={editingProd} categories={categories} subcategories={subcategories} ingredients={ingredients} onSaved={() => { setProdDialog(false); load(); }} />

      <SubcategoryManagerDialog
        open={!!subMgrCat}
        onOpenChange={(v) => { if (!v) setSubMgrCat(null); }}
        category={subMgrCat}
        subcategories={subcategories.filter((s) => subMgrCat && s.category_id === subMgrCat.id)}
        onChanged={load}
      />
    </div>
  );
}

function ProductDialog({ open, onOpenChange, product, categories, subcategories, ingredients, onSaved }) {
  const { t } = useTranslation();
  const blank = { name: '', category_id: '', subcategory_id: '', description: '', ingredients: [], allergens: [], diet_tags: [], price: 0, cost: 0, available: true, published: true, sales_channel: 'tavoli', barcode: '', station: 'cucina', image: null, image_x: 50, image_y: 50, image_zoom: 1, variants: [], extras: [], recipe: [] };
  const [f, setF] = useState(blank);
  const [ingInput, setIngInput] = useState('');

  useEffect(() => {
    if (open) { setF(product ? { ...blank, ...product, category_id: product.category_id || '', subcategory_id: product.subcategory_id || '' } : blank); setIngInput(''); }
  }, [open, product]);

  const catSubs = (subcategories || []).filter((s) => s.category_id === f.category_id);

  const save = async () => {
    if (!f.name.trim()) { toast.error(t('menu.nameRequired')); return; }
    const payload = { ...f, price: Number(f.price), cost: Number(f.cost), category_id: f.category_id || null, subcategory_id: f.subcategory_id || null, sales_channel: f.sales_channel === 'negozio' ? 'negozio' : 'tavoli', barcode: (f.sales_channel === 'negozio' && f.barcode) ? String(f.barcode).trim() : null };
    try {
      if (product) await api.put(`/products/${product.id}`, payload);
      else await api.post('/products', payload);
      toast.success(t('menu.productSaved')); onSaved();
    } catch (e) { toast.error(t('menu.saveError')); }
  };

  const addIngredient = () => {
    const parts = ingInput.split(',').map((x) => x.trim()).filter(Boolean);
    if (parts.length) setF((s) => ({ ...s, ingredients: [...(s.ingredients || []), ...parts] }));
    setIngInput('');
  };
  const toggleAllergen = (key) => setF((s) => {
    const set = new Set(s.allergens || []);
    set.has(key) ? set.delete(key) : set.add(key);
    return { ...s, allergens: Array.from(set) };
  });
  const toggleDiet = (key) => setF((s) => {
    const set = new Set(s.diet_tags || []);
    set.has(key) ? set.delete(key) : set.add(key);
    return { ...s, diet_tags: Array.from(set) };
  });

  const addVariant = () => setF((s) => ({ ...s, variants: [...s.variants, { name: '', price_delta: 0 }] }));
  const addExtra = () => setF((s) => ({ ...s, extras: [...s.extras, { name: '', price: 0 }] }));
  const addRecipe = () => setF((s) => ({ ...s, recipe: [...s.recipe, { ingredient_id: ingredients[0]?.id || '', qty: 1 }] }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader><DialogTitle>{product ? t('menu.editProduct') : t('menu.newProduct')}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>{t('menu.name')}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="product-name-input" /></div>
            <div>
              <Label>{t('menu.category')}</Label>
              <Select value={f.category_id} onValueChange={(v) => setF({ ...f, category_id: v, subcategory_id: '' })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder={t('menu.selectPlaceholder')} /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {catSubs.length > 0 && (
              <div className="col-span-2">
                <Label>{t('menu.subcategoryOptional')}</Label>
                <Select value={f.subcategory_id || 'none'} onValueChange={(v) => setF({ ...f, subcategory_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="product-subcategory-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('menu.noSubcategory')}</SelectItem>
                    {catSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div><Label>{t('menu.price')}</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="mt-1.5" /></div>
              <div><Label>{t('menu.cost')}</Label><Input type="number" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="mt-1.5" /></div>
            </div>
          </div>

          {/* Canale di vendita (esclusivo): Tavoli oppure Vendita negozio */}
          <div>
            <Label className="mb-1.5 block">{t('menu.sellChannel', { defaultValue: 'Dove vendere' })}</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'tavoli', label: t('menu.channelTables', { defaultValue: 'Tavoli' }), Icon: Map },
                { value: 'negozio', label: t('menu.channelStore', { defaultValue: 'Vendita negozio' }), Icon: Store },
              ].map(({ value, label, Icon }) => {
                const active = (f.sales_channel || 'tavoli') === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setF({ ...f, sales_channel: value })}
                    data-testid={`product-channel-${value}`}
                    aria-pressed={active}
                    className={cn('flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted')}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t('menu.channelHint', { defaultValue: 'I prodotti "Vendita negozio" non appaiono ai tavoli né nella carta digitale.' })}
            </p>
          </div>

          {/* Codice a barre (EAN): solo per la Vendita negozio (scansione in cassa) */}
          {(f.sales_channel === 'negozio') && (
            <div>
              <Label className="mb-1.5 flex items-center gap-1.5"><Barcode className="h-4 w-4" /> {t('menu.barcodeEan', { defaultValue: 'Codice a barre (EAN)' })}</Label>
              <Input
                value={f.barcode || ''}
                onChange={(e) => setF({ ...f, barcode: e.target.value })}
                placeholder={t('menu.barcodePlaceholder', { defaultValue: 'Scansiona o digita il codice' })}
                className="font-mono"
                data-testid="product-barcode-input"
              />
            </div>
          )}

          {/* Preparation station (routes the order to Kitchen or Bar KDS) — solo per i Tavoli */}
          {(f.sales_channel !== 'negozio') && (
          <div>
            <Label className="mb-1.5 block">{t('menu.prepStation')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {STATIONS.map(({ value, labelKey, Icon }) => {
                const active = (f.station || 'cucina') === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setF({ ...f, station: value })}
                    data-testid={`product-station-${value}`}
                    aria-pressed={active}
                    className={cn('flex h-11 items-center justify-center gap-2 rounded-xl border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:bg-muted')}
                  >
                    <Icon className="h-4 w-4" /> {t(labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Product image (fixed proportions, adjustable framing) */}
          <ImageCropUploader
            value={f}
            onChange={setF}
            aspect={PRODUCT_ASPECT}
            label={t('menu.productImage')}
            testId="product-image-uploader"
          />

          {/* Carta digitale: description, ingredients, allergens */}
          <div>
            <Label>{t('menu.description')}</Label>
            <Textarea value={f.description || ''} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={t('menu.descriptionPlaceholder')} className="mt-1.5" rows={2} data-testid="product-description-input" />
          </div>

          <div>
            <Label>{t('menu.ingredients')}</Label>
            <div className="mt-1.5 flex gap-2">
              <Input
                value={ingInput}
                onChange={(e) => setIngInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addIngredient(); } }}
                placeholder={t('menu.ingredientPlaceholder')}
                data-testid="product-ingredient-input"
              />
              <Button type="button" variant="outline" className="rounded-lg shrink-0" onClick={addIngredient}><Plus className="h-4 w-4" /></Button>
            </div>
            {f.ingredients?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {f.ingredients.map((ing, i) => (
                  <span key={`${ing}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs">
                    {ing}
                    <button type="button" onClick={() => setF((s) => ({ ...s, ingredients: s.ingredients.filter((_, x) => x !== i) }))} className="text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label>{t('menu.allergens')}</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="product-allergens">
              {ALLERGENS.map((a) => {
                const active = (f.allergens || []).includes(a.key);
                return (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => toggleAllergen(a.key)}
                    className={cn('rounded-full border px-2.5 py-1 text-xs font-medium transition-colors', active ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted-foreground hover:bg-muted')}
                  >
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>{t('menu.dietTags', { defaultValue: 'Regime alimentare' })}</Label>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="product-diet-tags">
              {DIET_TAGS.map((key) => {
                const active = (f.diet_tags || []).includes(key);
                const meta = DIET_TAG_META[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => toggleDiet(key)}
                    data-testid={`diet-tag-${key}`}
                    aria-pressed={active}
                    className={cn('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors', active ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted-foreground hover:bg-muted')}
                  >
                    <span style={{ lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: dietIconSvg(key, 16) }} />
                    {t(`dietTags.${key}`, { defaultValue: meta.label })}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mostra in carta digitale */}
          <div className="flex items-center justify-between rounded-xl border border-border p-3">
            <div>
              <div className="text-sm font-medium">{t('menu.showInDigital')}</div>
              <div className="text-xs text-muted-foreground">{t('menu.showProductDesc')}</div>
            </div>
            <Switch checked={f.published !== false} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="product-publish-switch" />
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-1"><Label>{t('menu.variants')}</Label><Button size="sm" variant="outline" onClick={addVariant}><Plus className="h-3 w-3 mr-1" />{t('menu.variants')}</Button></div>
            {f.variants.map((v, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <Input placeholder={t('menu.variantNamePlaceholder')} value={v.name} onChange={(e) => setF((s) => { const a = [...s.variants]; a[i] = { ...a[i], name: e.target.value }; return { ...s, variants: a }; })} />
                <Input type="number" placeholder="+\u20ac" value={v.price_delta} onChange={(e) => setF((s) => { const a = [...s.variants]; a[i] = { ...a[i], price_delta: Number(e.target.value) }; return { ...s, variants: a }; })} className="w-24" />
                <Button variant="ghost" size="icon" onClick={() => setF((s) => ({ ...s, variants: s.variants.filter((_, x) => x !== i) }))}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>

          {/* Extras */}
          <div>
            <div className="flex items-center justify-between mb-1"><Label>{t('menu.extras')}</Label><Button size="sm" variant="outline" onClick={addExtra}><Plus className="h-3 w-3 mr-1" />Extra</Button></div>
            {f.extras.map((v, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <Input placeholder={t('menu.extraNamePlaceholder')} value={v.name} onChange={(e) => setF((s) => { const a = [...s.extras]; a[i] = { ...a[i], name: e.target.value }; return { ...s, extras: a }; })} />
                <Input type="number" placeholder="\u20ac" value={v.price} onChange={(e) => setF((s) => { const a = [...s.extras]; a[i] = { ...a[i], price: Number(e.target.value) }; return { ...s, extras: a }; })} className="w-24" />
                <Button variant="ghost" size="icon" onClick={() => setF((s) => ({ ...s, extras: s.extras.filter((_, x) => x !== i) }))}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>

          {/* Recipe */}
          <div>
            <div className="flex items-center justify-between mb-1"><Label>{t('menu.recipe')}</Label><Button size="sm" variant="outline" onClick={addRecipe}><Plus className="h-3 w-3 mr-1" />{t('menu.ingredient')}</Button></div>
            {f.recipe.map((r, i) => (
              <div key={i} className="flex gap-2 mb-1.5">
                <Select value={r.ingredient_id} onValueChange={(val) => setF((s) => { const a = [...s.recipe]; a[i] = { ...a[i], ingredient_id: val }; return { ...s, recipe: a }; })}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder={t('menu.ingredient')} /></SelectTrigger>
                  <SelectContent>{ingredients.map((ing) => <SelectItem key={ing.id} value={ing.id}>{ing.name} ({ing.unit})</SelectItem>)}</SelectContent>
                </Select>
                <Input type="number" placeholder={t('menu.qty')} value={r.qty} onChange={(e) => setF((s) => { const a = [...s.recipe]; a[i] = { ...a[i], qty: Number(e.target.value) }; return { ...s, recipe: a }; })} className="w-24" />
                <Button variant="ghost" size="icon" onClick={() => setF((s) => ({ ...s, recipe: s.recipe.filter((_, x) => x !== i) }))}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('menu.cancel')}</Button>
          <Button onClick={save} data-testid="product-save-button">{t('menu.saveProduct')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
